import React from "react"
import { GatsbyImage } from "gatsby-plugin-image"
import PropTypes from "prop-types"

import Card from "../../layout/card"
import { BodyText, InlineText, SubTitle } from "../../Typography/Typography"
import SmartLink from "../../layout/SmartLink/SmartLink"
import { BROWSE_SEARCH_PATH } from "../../../utils/paths"
import Separator from "../../Separator/Separator"

import {
  featuredTowns as featuredTownsStyles,
  mainContainer,
  photo as photoStyles,
  positive,
  townItem,
} from "./styles.module.scss"
import { childImageSharpType } from "../../../shared-prop-types"

const area = (regulatoryBody) =>
  regulatoryBody === "ci" ? "Scotland" : "England"

const regulator = {
  ci: "Care Inspectorate (CI)",
  cqc: "Care Quality Commission (CQC)",
}

const regulatorRatings = {
  ci: "Excellent, Very Good, Good, Adequate, Weak or Unsatisfactory",
  cqc: "Outstanding, Good, Requires Improvement, or Inadequate",
}

const careServiceTypeParam = (careServiceType, careType) =>
  careServiceType && careType === "care_home"
    ? `&care_provided[]=${careServiceType}`
    : ""

const goodAndAboveRegulatoryRatingParam = (regulatoryBody) =>
  ({
    ci: "&regulator_rating[]=6&regulator_rating[]=5&regulator_rating[]=4",
    cqc: "&regulator_rating[]=6&regulator_rating[]=4",
  }[regulatoryBody])

const browseTownLink = (
  locationName,
  careType,
  regulatoryBody,
  careServiceType,
) => {
  const baseUrl = `${BROWSE_SEARCH_PATH}?care_type=${careType}&distance=5&location=${locationName}`

  return `${baseUrl}${goodAndAboveRegulatoryRatingParam(
    regulatoryBody,
  )}${careServiceTypeParam(
    careServiceType,
    careType,
  )}&sort_by=regulators_rating`
}

const introText = (pageType, regulatoryBody) =>
  ({
    care_home: `In ${area(regulatoryBody)}, care homes`,
    home_care: `Home care agencies in ${area(regulatoryBody)}`,
    dementia: `In ${area(regulatoryBody)}, dementia care homes`,
  }[pageType])

const pluraliseCareHome = (count) => (count === 1 ? "care home" : "care homes")
const pluraliseHomeCare = (count) => (count === 1 ? "agency" : "agencies")

const TownPhoto = ({ photo }) => (
  <GatsbyImage
    image={photo.childImageSharp?.gatsbyImageData}
    alt=""
    className={photoStyles}
  />
)

const TownItem = ({
  town,
  linkPosition,
  careType,
  regulatoryBody,
  careServiceType,
}) => {
  const townLink = browseTownLink(
    town.name,
    careType,
    regulatoryBody,
    careServiceType,
  )

  const townCount = careServiceType ? town.count : town.ratedPositiveCount

  const townClickEvent = {
    name: "SEO - Filtered Directory - Feature Town Clicked",
    properties: {
      "Target Path": townLink,
      "Link Position": linkPosition + 1,
    },
  }

  return (
    <div className={townItem}>
      <Separator />
      <div className={mainContainer}>
        <div>
          <SmartLink to={townLink} clickEvent={townClickEvent} chevron>
            <InlineText brand>{town.name}</InlineText>
          </SmartLink>
          <BodyText>
            {townCount}{" "}
            {careType === "care_home"
              ? pluraliseCareHome(townCount)
              : pluraliseHomeCare(townCount)}{" "}
            rated{" "}
            <InlineText semibold className={positive}>
              Good
            </InlineText>{" "}
            or better.
          </BodyText>
        </div>
        {town.coverPhoto && <TownPhoto photo={town.coverPhoto} />}
      </div>
    </div>
  )
}

const title = (pageType) =>
  ({
    care_home: "care homes",
    home_care: "home care agencies",
    dementia: "dementia care homes",
  }[pageType])

const HighestRatedTowns = ({
  careType,
  regulatoryBody,
  locationName,
  featuredTowns,
  careServiceType,
}) => {
  const pageType = careServiceType || careType

  return (
    <Card noMarginAbove>
      <SubTitle>Looking for the best-rated {title(pageType)}?</SubTitle>
      <BodyText marginBottom>
        {introText(pageType, regulatoryBody)} are regulated by the{" "}
        <InlineText semibold>{regulator[regulatoryBody]}</InlineText> which
        rates services as {regulatorRatings[regulatoryBody]}.
      </BodyText>
      <BodyText marginBottom semibold>
        Search featured towns in {locationName}
      </BodyText>
      <div className={featuredTownsStyles}>
        {featuredTowns.map((town, linkPosition) => (
          <TownItem
            key={town.name}
            town={town}
            linkPosition={linkPosition}
            careType={careType}
            regulatoryBody={regulatoryBody}
            careServiceType={careServiceType}
          />
        ))}
      </div>
    </Card>
  )
}

export default HighestRatedTowns

HighestRatedTowns.propTypes = {
  careType: PropTypes.oneOf(["home_care", "care_home"]).isRequired,
  regulatoryBody: PropTypes.oneOf(["ci", "cqc"]).isRequired,
  locationName: PropTypes.string.isRequired,
  featuredTowns: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      ratedPositiveCount: PropTypes.number,
      count: PropTypes.number,
      coverPhoto: childImageSharpType,
    }),
  ).isRequired,
  careServiceType: PropTypes.oneOf(["dementia"]),
}

HighestRatedTowns.defaultProps = {
  careServiceType: undefined,
}

TownPhoto.propTypes = {
  photo: childImageSharpType.isRequired,
}

TownItem.propTypes = {
  town: PropTypes.shape({
    name: PropTypes.string,
    ratedPositiveCount: PropTypes.number,
    count: PropTypes.number,
    coverPhoto: childImageSharpType,
  }).isRequired,
  linkPosition: PropTypes.number.isRequired,
  careType: PropTypes.oneOf(["home_care", "care_home"]).isRequired,
  regulatoryBody: PropTypes.oneOf(["ci", "cqc"]).isRequired,
  careServiceType: PropTypes.oneOf(["dementia"]),
}

TownItem.defaultProps = {
  careServiceType: undefined,
}
